import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, Edit, Plus, Trash2, Phone, Mail, MapPin, Building, 
  Calendar, FileText, Users, Euro, Folder, Paperclip, ChevronDown, ChevronUp,
  User, Search, Shield, Key, Send, Loader2, CheckSquare, Clock,
  AlertTriangle, Landmark, Scale, Copy, Check
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { contactosAPI, expedientesAPI, facturasAPI, eventosAPI } from '../api';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import AdjuntosSection from '../components/AdjuntosSection';
import { ContactoWizard } from './ContactosNew';
import { descargarFichero, obtenerToken } from '../lib/auth';

// Subtipos organizados por tipo de entidad (igual que en ContactosNew)
const SUBTIPOS_POR_ENTIDAD = {
  persona: [
    { codigo: 'cliente', nombre: 'Cliente' },
    { codigo: 'potencial_cliente', nombre: 'Potencial Cliente' },
    { codigo: 'abogado_propio', nombre: 'Abogado Propio' },
    { codigo: 'abogado_contrario', nombre: 'Abogado Contrario' },
    { codigo: 'abogado_colaborador', nombre: 'Abogado Colaborador' },
    { codigo: 'procurador_propio', nombre: 'Procurador Propio' },
    { codigo: 'procurador_contrario', nombre: 'Procurador Contrario' },
    { codigo: 'parte_contraria', nombre: 'Parte Contraria' },
    { codigo: 'asesor', nombre: 'Asesor' },
    { codigo: 'colaborador', nombre: 'Colaborador' },
    { codigo: 'gestor', nombre: 'Gestor' },
    { codigo: 'intermediario', nombre: 'Intermediario' },
    { codigo: 'otorgante', nombre: 'Otorgante' },
    { codigo: 'perito', nombre: 'Perito' },
    { codigo: 'testigo_propio', nombre: 'Testigo Propio' },
    { codigo: 'testigo_contrario', nombre: 'Testigo Parte Contraria' },
    { codigo: 'proveedor', nombre: 'Proveedor' },
    { codigo: 'otro', nombre: 'Otro' }
  ],
  empresa: [
    { codigo: 'cliente', nombre: 'Cliente' },
    { codigo: 'potencial_cliente', nombre: 'Potencial Cliente' },
    { codigo: 'parte_contraria', nombre: 'Parte Contraria' },
    { codigo: 'sociedad_limitada', nombre: 'Sociedad Limitada' },
    { codigo: 'sociedad_anonima', nombre: 'Sociedad Anónima' },
    { codigo: 'comunidad_bienes', nombre: 'Comunidad de Bienes' },
    { codigo: 'sociedad_civil', nombre: 'Sociedad Civil' },
    { codigo: 'cooperativa', nombre: 'Cooperativa' },
    { codigo: 'asesoria', nombre: 'Asesoría' },
    { codigo: 'gestoria', nombre: 'Gestoría' },
    { codigo: 'aseguradora', nombre: 'Aseguradora' },
    { codigo: 'inmobiliaria', nombre: 'Inmobiliaria' },
    { codigo: 'colaborador', nombre: 'Colaborador' },
    { codigo: 'otorgante', nombre: 'Otorgante' },
    { codigo: 'perito', nombre: 'Perito' },
    { codigo: 'proveedor', nombre: 'Proveedor' },
    { codigo: 'otro', nombre: 'Otro' }
  ],
  organismo: [
    { codigo: 'juzgado', nombre: 'Juzgado' },
    { codigo: 'organismo', nombre: 'Organismo' },
    { codigo: 'notaria', nombre: 'Notaría' },
    { codigo: 'registro', nombre: 'Registro' },
    { codigo: 'administracion_publica', nombre: 'Administración Pública' },
    { codigo: 'diputacion', nombre: 'Diputación' },
    { codigo: 'cliente', nombre: 'Cliente' },
    { codigo: 'proveedor', nombre: 'Proveedor' },
    { codigo: 'otro', nombre: 'Otro' }
  ]
};

// Lista plana de todos los subtipos únicos
const TODOS_LOS_SUBTIPOS = [
  ...SUBTIPOS_POR_ENTIDAD.persona,
  ...SUBTIPOS_POR_ENTIDAD.empresa.filter(s => !SUBTIPOS_POR_ENTIDAD.persona.find(p => p.codigo === s.codigo)),
  ...SUBTIPOS_POR_ENTIDAD.organismo.filter(s => !SUBTIPOS_POR_ENTIDAD.persona.find(p => p.codigo === s.codigo) && !SUBTIPOS_POR_ENTIDAD.empresa.find(e => e.codigo === s.codigo))
];

// Compatibilidad con código existente
const tipos = TODOS_LOS_SUBTIPOS.map(s => ({ value: s.codigo, label: s.nombre }));

// Componente de sección colapsable
const Section = ({ title, icon: Icon, children, defaultOpen = false, badge = null }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button className="w-full flex items-center justify-between p-4 bg-blue-800 text-white hover:bg-blue-900 transition-colors">
          <div className="flex items-center gap-2">
            <Icon className="w-5 h-5" />
            <span className="font-medium">{title}</span>
            {badge !== null && (
              <Badge className="bg-white text-blue-800 ml-2">{badge}</Badge>
            )}
          </div>
          {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="border border-t-0 border-gray-200 bg-white p-4">
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// Componente de tabla vacía
const EmptyTable = ({ columns, message = "No hay datos" }) => (
  <div className="overflow-x-auto">
    <table className="w-full">
      <thead className="bg-gray-50 border-b">
        <tr>
          {columns.map((col, idx) => (
            <th key={idx} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {col}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          <td colSpan={columns.length} className="px-4 py-8 text-center text-gray-500">
            {message}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
);

const ContactoDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contacto, setContacto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [expedientes, setExpedientes] = useState([]);
  const [searchTermExp, setSearchTermExp] = useState("");
  const [expedientesComoJuzgado, setExpedientesComoJuzgado] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [estafasBancarias, setEstafasBancarias] = useState([]);
  const [reclamacionesExtrajudiciales, setReclamacionesExtrajudiciales] = useState([]);
  const [notasExpedientes, setNotasExpedientes] = useState([]);
  
  // Estados para múltiples valores
  const [telefonos, setTelefonos] = useState([]);
  const [emails, setEmails] = useState([]);
  const [direcciones, setDirecciones] = useState([]);
  const [editingField, setEditingField] = useState(null);
  
  // Copiar datos
  const [copied, setCopied] = useState(false);

  // Portal del cliente
  const [showPortalDialog, setShowPortalDialog] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalEmail, setPortalEmail] = useState('');
  const [portalExpedientes, setPortalExpedientes] = useState([]);
  const [allExpedientes, setAllExpedientes] = useState([]);

  useEffect(() => {
    loadContacto();
  }, [id]);

  const loadContacto = async () => {
    try {
      const [contactoRes, expRes, factRes] = await Promise.all([
        contactosAPI.getById(id),
        contactosAPI.getExpedientes(id),
        contactosAPI.getFacturas(id)
      ]);
      
      const data = contactoRes.data;
      setContacto(data);
      setExpedientes(expRes.data || []);
      setFacturas(factRes.data || []);
      
      // Cargar tareas de forma separada para que no rompa si falla
      try {
        const tareasRes = await contactosAPI.getTareas(id);
        setTareas(tareasRes.data || []);
      } catch (e) {
        console.log('No se pudieron cargar las tareas:', e);
        setTareas([]);
      }
      
      // Cargar estafas bancarias
      try {
        const estafasRes = await contactosAPI.getEstafasBancarias(id);
        setEstafasBancarias(estafasRes.data || []);
      } catch (e) {
        console.log('No se pudieron cargar las estafas bancarias:', e);
        setEstafasBancarias([]);
      }
      
      // Cargar reclamaciones extrajudiciales (v116: endpoint directo por cliente_id)
      try {
        const _tokV116 = obtenerToken();
        const reclamacionesRes = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/extrajudicial/reclamaciones`,
          { params: { cliente_id: id, limit: 200 }, headers: _tokV116 ? { Authorization: `Bearer ${_tokV116}` } : {} }
        );
        const _dataV116 = Array.isArray(reclamacionesRes.data) ? reclamacionesRes.data : (reclamacionesRes.data?.items || []);
        setReclamacionesExtrajudiciales(_dataV116);
      } catch (e) {
        console.log('No se pudieron cargar las reclamaciones extrajudiciales:', e);
        setReclamacionesExtrajudiciales([]);
      }
      
      // Cargar notas de expedientes
      try {
        const notasRes = await contactosAPI.getNotas(id);
        setNotasExpedientes(notasRes.data || []);
      } catch (e) {
        setNotasExpedientes([]);
      }
      
      // Si es organismo, cargar expedientes donde actúa como juzgado
      if (data.tipo === 'organismo') {
        try {
          const expJuzgadoRes = await expedientesAPI.getByJuzgado(id);
          setExpedientesComoJuzgado(expJuzgadoRes.data || []);
        } catch (err) {
          console.log('No hay expedientes como juzgado');
          setExpedientesComoJuzgado([]);
        }
      }
      
      // v118: construir arrays desde los campos reales del backend (multivalor visible)
      const _tels118 = (data.telefonos && data.telefonos.length) ? data.telefonos : [
        { tipo: 'Móvil', numero: data.telefono_movil || '' },
        { tipo: 'Fijo', numero: data.telefono || '' },
        { tipo: 'Secundario', numero: data.telefono_secundario || '' },
        { tipo: 'Fax', numero: data.fax || '' },
      ].filter((t) => t.numero);
      setTelefonos(_tels118.length ? _tels118 : [{ tipo: 'Móvil', numero: '' }]);

      const _emails118 = (data.emails_list && data.emails_list.length) ? data.emails_list : [
        { tipo: 'Principal', email: data.email || '' },
        { tipo: 'Secundario', email: data.email_secundario || '' },
      ].filter((e) => e.email);
      setEmails(_emails118.length ? _emails118 : [{ tipo: 'Principal', email: '' }]);

      setDirecciones(data.direcciones || [{
        tipo: 'Principal',
        direccion: [data.direccion, data.direccion_linea2].filter(Boolean).join(' - '),
        ciudad: data.ciudad || '',
        codigo_postal: data.codigo_postal || '',
        provincia: data.provincia || ''
      }]);
      
    } catch (error) {
      console.error('Error cargando contacto:', error);
      toast({
        title: "Error",
        description: "No se pudo cargar el contacto",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateContacto = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      const data = {
        nombre: formData.get('nombre'),
        primer_apellido: formData.get('primer_apellido'),
        segundo_apellido: formData.get('segundo_apellido'),
        tipo: formData.get('tipo') || contacto.tipo,
        nif_cif: formData.get('nif_cif'),
        email: emails[0]?.email || formData.get('email'),
        telefono: telefonos[0]?.numero || formData.get('telefono'),
        direccion: direcciones[0]?.direccion || formData.get('direccion'),
        ciudad: direcciones[0]?.ciudad || formData.get('ciudad'),
        codigo_postal: direcciones[0]?.codigo_postal || formData.get('codigo_postal'),
        provincia: direcciones[0]?.provincia || formData.get('provincia'),
        notas: formData.get('notas')
      };

      await contactosAPI.update(id, data);
      toast({
        title: "Contacto actualizado",
        description: "Los datos se han guardado correctamente"
      });
      setIsEditDialogOpen(false);
      loadContacto();
    } catch (error) {
      console.error('Error actualizando contacto:', error);
      toast({
        title: "Error",
        description: "No se pudo actualizar el contacto",
        variant: "destructive"
      });
    }
  };

  const addTelefono = () => {
    setTelefonos([...telefonos, { tipo: 'Otro', numero: '' }]);
  };

  const addEmail = () => {
    setEmails([...emails, { tipo: 'Otro', email: '' }]);
  };

  const addDireccion = () => {
    setDirecciones([...direcciones, { tipo: 'Otro', direccion: '', ciudad: '', codigo_postal: '', provincia: '' }]);
  };

  const removeTelefono = (index) => {
    setTelefonos(telefonos.filter((_, i) => i !== index));
  };

  const removeEmail = (index) => {
    setEmails(emails.filter((_, i) => i !== index));
  };

  const removeDireccion = (index) => {
    setDirecciones(direcciones.filter((_, i) => i !== index));
  };

  // Funciones para Portal del Cliente
  const handleOpenPortalDialog = async () => {
    setPortalEmail(contacto.email || '');
    setPortalExpedientes([]);
    try {
      const expRes = await expedientesAPI.getAll();
      setAllExpedientes(expRes.data || []);
    } catch (err) {
      console.error('Error cargando expedientes:', err);
    }
    setShowPortalDialog(true);
  };

  const handleDarAccesoPortal = async () => {
    if (!portalEmail) {
      toast({ title: 'Error', description: 'Ingrese un email', variant: 'destructive' });
      return;
    }
    
    setPortalLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal-cliente/registrar-con-envio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contacto_id: parseInt(id),
          email: portalEmail,
          expedientes_ids: portalExpedientes.map(e => parseInt(e))
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast({
          title: 'Acceso creado',
          description: data.email_enviado 
            ? `Código enviado a ${portalEmail}` 
            : `Registrado. Código: ${data.codigo_acceso} (Configure email para envío automático)`
        });
        setShowPortalDialog(false);
      } else {
        throw new Error(data.detail || 'Error al crear acceso');
      }
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.message || 'No se pudo crear el acceso', 
        variant: 'destructive' 
      });
    } finally {
      setPortalLoading(false);
    }
  };

  const handleCopyData = async () => {
    const lines = [];
    
    // Nombre completo
    const nombreCompleto = [contacto.nombre, contacto.primer_apellido, contacto.segundo_apellido]
      .filter(Boolean).join(' ') || contacto.razon_social || '';
    if (nombreCompleto) lines.push(`Nombre: ${nombreCompleto}`);
    
    // NIF/CIF
    if (contacto.nif_cif) lines.push(`NIF/CIF: ${contacto.nif_cif}`);
    
    // Dirección/es
    direcciones.forEach((dir) => {
      const partes = [dir.direccion, dir.codigo_postal, dir.ciudad, dir.provincia].filter(Boolean);
      if (partes.length > 0) {
        lines.push(`Dirección: ${partes.join(', ')}`);
      }
    });
    
    // Teléfono/s
    telefonos.forEach((tel) => {
      if (tel.numero) {
        lines.push(`Teléfono: ${tel.numero}`);
      }
    });
    
    // Email/s
    emails.forEach((em) => {
      if (em.email) {
        lines.push(`Email: ${em.email}`);
      }
    });
    
    const text = lines.join('\n');
    
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast({
        title: "Datos copiados",
        description: "Los datos del contacto se han copiado al portapapeles"
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback para navegadores sin API clipboard
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      toast({
        title: "Datos copiados",
        description: "Los datos del contacto se han copiado al portapapeles"
      });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getTipoBadge = (tipo) => {
    const colors = {
      cliente: 'bg-blue-100 text-blue-700',
      contraparte: 'bg-red-100 text-red-700',
      procurador: 'bg-purple-100 text-purple-700',
      perito: 'bg-green-100 text-green-700',
      otro: 'bg-gray-100 text-gray-700'
    };
    return colors[tipo] || colors.otro;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!contacto) {
    return (
      <div className="p-6">
        <p className="text-center text-gray-500">Contacto no encontrado</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/contactos')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Contacto | {[contacto.nombre, contacto.primer_apellido, contacto.segundo_apellido].filter(Boolean).join(' ') || contacto.razon_social}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {contacto.tipo !== 'organismo' && (
            <Button 
              onClick={handleOpenPortalDialog} 
              variant="outline"
              className="border-blue-200 text-blue-700 hover:bg-blue-50"
            >
              <Shield className="w-4 h-4 mr-2" />
              Dar acceso al portal
            </Button>
          )}
          <Button onClick={() => setIsEditDialogOpen(true)} className="bg-green-600 hover:bg-green-700">
            <Edit className="w-4 h-4 mr-2" />
            Editar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Columna izquierda - Datos del contacto */}
        <div className="lg:col-span-1">
          <Card>
            <CardContent className="p-6">
              {/* Avatar */}
              <div className="flex flex-col items-center mb-6">
                <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-green-600 rounded-lg flex items-center justify-center mb-4">
                  <User className="w-12 h-12 text-white" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">
                  {[contacto.nombre, contacto.primer_apellido, contacto.segundo_apellido].filter(Boolean).join(' ') || contacto.razon_social || 'Sin nombre'}
                </h2>
                <p className="text-gray-500">
                  {contacto.tipo_entidad || 'Persona'} - <Badge className={getTipoBadge(contacto.tipo)}>
                    {tipos.find(t => t.value === contacto.tipo)?.label || contacto.tipo}
                  </Badge>
                </p>
              </div>

              {/* Datos de contacto */}
              <div className="space-y-4">
                {/* NIF/CIF */}
                {contacto.nif_cif && (
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">NIF/CIF</p>
                      <p className="font-medium">{contacto.nif_cif}</p>
                    </div>
                  </div>
                )}

                {/* Teléfonos */}
                {telefonos.map((tel, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">{tel.tipo}</p>
                      {tel.numero ? (
                        <a href={`tel:${tel.numero}`} className="font-medium text-blue-600 hover:underline">
                          {tel.numero}
                        </a>
                      ) : (
                        <p className="text-gray-400">-</p>
                      )}
                    </div>
                  </div>
                ))}

                {/* Emails */}
                {emails.map((em, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">{em.tipo}</p>
                      {em.email ? (
                        <a href={`mailto:${em.email}`} className="font-medium text-blue-600 hover:underline break-all">
                          {em.email}
                        </a>
                      ) : (
                        <p className="text-gray-400">-</p>
                      )}
                    </div>
                  </div>
                ))}

                {/* Direcciones */}
                {direcciones.map((dir, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">{dir.tipo}</p>
                      <p className="font-medium">
                        {[dir.direccion, dir.codigo_postal, dir.ciudad, dir.provincia]
                          .filter(Boolean).join(', ') || '-'}
                      </p>
                    </div>
                  </div>
                ))}

                {/* Notas */}
                {contacto.notas && (
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Notas</p>
                      <p className="font-medium">{contacto.notas}</p>
                    </div>
                  </div>
                )}

                {/* Fecha de creación */}
                <div className="pt-4 border-t">
                  <p className="text-sm text-gray-500">
                    Fecha de creación: {new Date(contacto.created_at).toLocaleDateString('es-ES')}
                  </p>
                </div>

                {/* Botón Copiar datos */}
                <div className="pt-4">
                  <Button
                    data-testid="copy-contact-data-btn"
                    variant="outline"
                    className="w-full"
                    onClick={handleCopyData}
                  >
                    {copied ? (
                      <Check className="w-4 h-4 mr-2 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4 mr-2" />
                    )}
                    {copied ? 'Datos copiados' : 'Copiar datos'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Columna derecha - Secciones */}
        <div className="lg:col-span-2 space-y-0">
          {/* Eventos */}
          <Section title="Eventos" icon={Calendar} defaultOpen={true} badge={eventos.length}>
            <div className="flex justify-between items-center mb-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input placeholder="Busca en la tabla" className="pl-10" value={searchTermExp} onChange={(e) => setSearchTermExp(e.target.value)} />
              </div>
              <Button size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Evento
              </Button>
            </div>
            <EmptyTable columns={['Tipo', 'Título', 'Fecha de comienzo', 'Fecha de fin', 'Calendario', 'Estado', 'Acciones']} />
          </Section>

          {/* Adjuntos */}
          <Section title="Adjuntos" icon={Paperclip} badge={0}>
            <AdjuntosSection 
              entityType="contacto" 
              entityId={parseInt(id)} 
              showHeader={false}
            />
          </Section>

          {/* Tareas pendientes */}
          <Section title="Tareas pendientes" icon={CheckSquare} badge={tareas.length}>
            {tareas.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No hay tareas pendientes con este contacto</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Título</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Exp. Judicial</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prioridad</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Asignado a</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha límite</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {tareas.map((tarea, index) => (
                      <tr key={`${tarea.tipo}-${tarea.id}-${index}`} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{tarea.titulo}</p>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={tarea.tipo === 'atencion_cliente' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
                            {tarea.tipo === 'atencion_cliente' ? 'Atención cliente' : 'Correo'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={
                            tarea.estado === 'pendiente' ? 'bg-yellow-100 text-yellow-700' :
                            tarea.estado === 'en_progreso' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }>
                            {tarea.estado?.replace(/_/g, ' ') || 'Pendiente'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={
                            tarea.prioridad === 'alta' ? 'bg-red-100 text-red-700' :
                            tarea.prioridad === 'media' ? 'bg-orange-100 text-orange-700' :
                            'bg-green-100 text-green-700'
                          }>
                            {tarea.prioridad || 'Media'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{tarea.asignado_a || '-'}</td>
                        <td className="px-4 py-3">
                          {tarea.fecha_limite ? (
                            <div className="flex items-center gap-1 text-gray-600">
                              <Clock className="w-4 h-4" />
                              {new Date(tarea.fecha_limite).toLocaleDateString('es-ES')}
                            </div>
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* Expedientes legales */}
          <Section title="Expedientes legales" icon={Folder} badge={expedientes.length}>
            <div className="flex justify-between items-center mb-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input placeholder="Busca en la tabla" className="pl-10" value={searchTermExp} onChange={(e) => setSearchTermExp(e.target.value)} />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => descargarFichero(`${process.env.REACT_APP_BACKEND_URL}/api/contactos/${id}/exportar-pdf`, `contacto-${id}.pdf`)}>Exportar PDF</Button>
                <Button size="sm" onClick={() => navigate(`/expedientes?crear=true&contacto_id=${id}`)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo
                </Button>
              </div>
            </div>
            {expedientes.length === 0 ? (
              <EmptyTable columns={['Serie', 'Código', 'Procedimiento', 'Rol', 'Fecha', 'Descripción', 'Acciones']} />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Título</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rol</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Exp. Judicial</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Juzgado</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {expedientes.filter(exp => !searchTermExp || exp.numero_expediente?.toLowerCase().includes(searchTermExp.toLowerCase()) || exp.titulo?.toLowerCase().includes(searchTermExp.toLowerCase())).map((exp) => (
                      <tr key={exp.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <Link to={`/expedientes/${exp.id}`} className="text-blue-600 hover:underline font-medium">
                            {exp.numero_expediente}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{exp.titulo}</td>
                        <td className="px-4 py-3">
                          <Badge className="bg-blue-100 text-blue-700 capitalize">
                            {exp.rol?.replace(/_/g, ' ') || 'Cliente'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{exp.expediente_judicial || '-'}</td>
                        <td className="px-4 py-3 text-gray-600">{exp.juzgado || '-'}</td>
                        <td className="px-4 py-3 text-right">
                          <Button variant="ghost" size="sm" asChild>
                            <Link to={`/expedientes/${exp.id}`}>Ver</Link>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* Expedientes como Juzgado (solo para organismos) */}
          {contacto?.tipo === 'organismo' && (
            <Section title="Expedientes asociados como Juzgado" icon={Building} badge={expedientesComoJuzgado.length} defaultOpen={expedientesComoJuzgado.length > 0}>
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Expedientes donde este organismo está asignado como juzgado
                </p>
              </div>
              {expedientesComoJuzgado.length === 0 ? (
                <EmptyTable columns={['Código', 'Exp. Judicial', 'NIG', 'Título', 'Estado', 'Fecha', 'Acciones']} message="No hay expedientes asignados" />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Exp. Judicial</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">NIG</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Título</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Exp. Judicial</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Juzgado</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {expedientesComoJuzgado.map((exp) => (
                        <tr key={exp.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <Link to={`/expedientes/${exp.id}`} className="text-blue-600 hover:underline font-medium">
                              {exp.numero_expediente}
                            </Link>
                          </td>
                          <td className="px-4 py-3 text-gray-600">{exp.expediente_judicial || '-'}</td>
                          <td className="px-4 py-3 text-gray-600">{exp.nig || '-'}</td>
                          <td className="px-4 py-3 text-gray-600">{exp.titulo}</td>
                          <td className="px-4 py-3">
                            <Badge className={exp.estado === 'activo' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                              {exp.estado}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {exp.fecha_inicio ? new Date(exp.fecha_inicio).toLocaleDateString('es-ES') : '-'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button variant="ghost" size="sm" asChild>
                              <Link to={`/expedientes/${exp.id}`}>Ver</Link>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>
          )}

          {/* Estafas Bancarias */}
          <Section title="Estafas Bancarias" icon={AlertTriangle} badge={estafasBancarias.length} defaultOpen={estafasBancarias.length > 0}>
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Casos de estafas bancarias donde este contacto está involucrado
              </p>
            </div>
            {estafasBancarias.length === 0 ? (
              <EmptyTable columns={['Referencia', 'Tipo Estafa', 'Banco', 'Importe', 'Estado', 'Fecha', 'Rol', 'Acciones']} message="No hay casos de estafas bancarias" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referencia</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo Estafa</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Banco</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Importe</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Exp. Judicial</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Juzgado</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rol</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {estafasBancarias.map((caso) => (
                      <tr key={caso.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <Link to={`/estafas-bancarias/${caso.id}`} className="text-blue-600 hover:underline font-medium">
                            {caso.numero_referencia}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-gray-600 capitalize">{caso.tipo_estafa?.replace('_', ' ') || '-'}</td>
                        <td className="px-4 py-3 text-gray-600">{caso.banco_nombre || '-'}</td>
                        <td className="px-4 py-3 font-medium text-red-600">
                          {caso.cantidad_estafada ? `${caso.cantidad_estafada.toLocaleString('es-ES')}€` : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={
                            caso.estado === 'expediente_creado' ? 'bg-green-100 text-green-700' :
                            caso.estado === 'inicio' ? 'bg-gray-100 text-gray-700' :
                            'bg-yellow-100 text-yellow-700'
                          }>
                            {caso.estado?.replace('_', ' ') || 'inicio'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {caso.fecha_fraude ? new Date(caso.fecha_fraude).toLocaleDateString('es-ES') : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={caso.rol === 'cliente' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}>
                            {caso.rol === 'cliente' ? 'Cliente/Víctima' : 'Entidad Bancaria'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button variant="ghost" size="sm" asChild>
                            <Link to={`/estafas-bancarias/${caso.id}`}>Ver</Link>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* Reclamaciones Extrajudiciales */}
          <Section title="Reclamaciones Extrajudiciales" icon={Scale} badge={reclamacionesExtrajudiciales.length} defaultOpen={reclamacionesExtrajudiciales.length > 0}>
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Reclamaciones extrajudiciales (gastos hipotecarios, cláusulas abusivas, etc.) de este contacto
              </p>
            </div>
            {reclamacionesExtrajudiciales.length === 0 ? (
              <EmptyTable columns={['Referencia', 'Entidad Bancaria', 'Cláusulas', 'Estado', 'Fecha Hipoteca', 'Acciones']} message="No hay reclamaciones extrajudiciales" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referencia</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entidad Bancaria</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cláusulas</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Exp. Judicial</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Hipoteca</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {reclamacionesExtrajudiciales.map((rec) => {
                      // Contar cláusulas activas
                      const clausulasActivas = [];
                      if (rec.clausulas?.gastos) clausulasActivas.push('Gastos');
                      if (rec.clausulas?.comision_apertura) clausulasActivas.push('Com. Apertura');
                      if (rec.clausulas?.suelo) clausulasActivas.push('Suelo');
                      if (rec.clausulas?.redondeo) clausulasActivas.push('Redondeo');
                      if (rec.clausulas?.interes_demora) clausulasActivas.push('Int. Demora');
                      if (rec.clausulas?.cierre_irph) clausulasActivas.push('IRPH');
                      
                      return (
                        <tr key={rec.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <Link to={`/extrajudicial/${rec.id}`} className="text-blue-600 hover:underline font-medium">
                              {rec.numero_referencia}
                            </Link>
                          </td>
                          <td className="px-4 py-3 text-gray-600">{rec.entidad_bancaria_actual || '-'}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {clausulasActivas.length > 0 ? (
                                clausulasActivas.map((cl, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs">
                                    {cl}
                                  </Badge>
                                ))
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={
                              rec.estado === 'finalizado' ? 'bg-green-100 text-green-700' :
                              rec.estado === 'en_tramite' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }>
                              {rec.estado?.replace('_', ' ') || 'inicio'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {rec.fecha_firma_hipoteca ? new Date(rec.fecha_firma_hipoteca).toLocaleDateString('es-ES') : '-'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button variant="ghost" size="sm" asChild>
                              <Link to={`/extrajudicial/${rec.id}`}>Ver</Link>
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* Notas */}
          <Section title="Notas de Expedientes" icon={FileText} badge={notasExpedientes.length}>
            {notasExpedientes.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No hay notas en los expedientes de este contacto</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Título</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contenido</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expediente</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {notasExpedientes.map((nota) => (
                      <tr key={nota.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-600">{nota.fecha ? new Date(nota.fecha).toLocaleDateString('es-ES') : '-'}</td>
                        <td className="px-4 py-3 font-medium">{nota.titulo}</td>
                        <td className="px-4 py-3 text-gray-600">{nota.contenido || '-'}</td>
                        <td className="px-4 py-3"><Link to={`/expedientes/${nota.expediente_id}`} className="text-blue-600 hover:underline">{nota.expediente_codigo}</Link></td>
                        <td className="px-4 py-3 text-right"><Button variant="ghost" size="sm" asChild><Link to={`/expedientes/${nota.expediente_id}`}>Ver expediente</Link></Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* Contactos relacionados */}
          <Section title="Contactos" icon={Users} badge={0}>
            <div className="flex justify-between items-center mb-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input placeholder="Busca en la tabla" className="pl-10" value={searchTermExp} onChange={(e) => setSearchTermExp(e.target.value)} />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm">Borrar selección</Button>
                <Button size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo
                </Button>
              </div>
            </div>
            <EmptyTable columns={['Nombre', 'Tipo', 'Subtipo', 'CIF/NIF', 'Fecha nacimiento', 'Sitio web', 'Acciones']} />
          </Section>

          {/* Facturas */}
          <Section title="Facturas" icon={Euro} badge={facturas.length}>
            <div className="flex justify-between items-center mb-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input placeholder="Busca en la tabla" className="pl-10" value={searchTermExp} onChange={(e) => setSearchTermExp(e.target.value)} />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm">Descargar</Button>
                <Button variant="outline" size="sm">Enviar</Button>
                <Button size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Nueva
                </Button>
              </div>
            </div>
            {facturas.length === 0 ? (
              <EmptyTable columns={['Tipo', 'Nº Factura', 'Fecha', 'Concepto', 'Total', 'Estado', 'Acciones']} />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nº Factura</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Concepto</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Exp. Judicial</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Juzgado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {facturas.map((fac) => (
                      <tr key={fac.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-blue-600">{fac.numero_factura}</td>
                        <td className="px-4 py-3 text-gray-600">{fac.concepto}</td>
                        <td className="px-4 py-3 font-medium">{fac.total?.toLocaleString('es-ES')}€</td>
                        <td className="px-4 py-3">
                          <Badge className={fac.estado === 'pagada' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                            {fac.estado}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {fac.fecha_emision ? new Date(fac.fecha_emision).toLocaleDateString('es-ES') : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* Emails */}
          <Section title="Emails" icon={Mail} badge={0}>
            <div className="flex justify-between items-center mb-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input placeholder="Busca en la tabla" className="pl-10" value={searchTermExp} onChange={(e) => setSearchTermExp(e.target.value)} />
              </div>
            </div>
            <EmptyTable columns={['Fecha', 'De', 'Asunto', 'Para', 'Acciones']} />
          </Section>

          {/* Conceptos facturables */}
          <Section title="Conceptos facturables" icon={Euro} badge={0}>
            <div className="flex justify-between items-center mb-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input placeholder="Busca en la tabla" className="pl-10" value={searchTermExp} onChange={(e) => setSearchTermExp(e.target.value)} />
              </div>
              <Button size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo
              </Button>
            </div>
            <EmptyTable columns={['Fecha', 'Título', 'Cliente', 'Expediente', 'Facturado', 'Nº Factura', 'Coste total', 'Acciones']} />
          </Section>
        </div>
      </div>

      {/* Dialog de edición — formulario unificado con creación (ContactoWizard) */}
      <ContactoWizard
        isOpen={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        contacto={contacto}
        onSaved={loadContacto}
      />

      {/* Dialog: Dar acceso al Portal del Cliente */}
      <Dialog open={showPortalDialog} onOpenChange={setShowPortalDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              Dar acceso al Portal del Cliente
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Al dar acceso, el cliente podrá consultar el estado de sus expedientes y enviar consultas.
              Se generará un código de acceso automáticamente y se enviará por email.
            </p>
            
            <div>
              <Label>Email de acceso *</Label>
              <Input 
                type="email"
                value={portalEmail}
                onChange={(e) => setPortalEmail(e.target.value)}
                placeholder="email@ejemplo.com"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Expedientes a los que tendrá acceso</Label>
              <div className="border rounded-lg max-h-[200px] overflow-y-auto mt-1">
                {allExpedientes.length === 0 ? (
                  <p className="text-gray-500 text-sm p-4 text-center">No hay expedientes</p>
                ) : (
                  allExpedientes.map(exp => (
                    <label 
                      key={exp.id}
                      className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                    >
                      <input 
                        type="checkbox"
                        checked={portalExpedientes.includes(exp.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setPortalExpedientes([...portalExpedientes, exp.id]);
                          } else {
                            setPortalExpedientes(portalExpedientes.filter(id => id !== exp.id));
                          }
                        }}
                        className="w-4 h-4 text-blue-600"
                      />
                      <div>
                        <p className="font-medium text-sm">{exp.numero_expediente}</p>
                        <p className="text-xs text-gray-500">{exp.titulo}</p>
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPortalDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleDarAccesoPortal}
              disabled={portalLoading || !portalEmail}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {portalLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Key className="w-4 h-4 mr-2" />
              )}
              Crear acceso y enviar código
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContactoDetalle;
